/**
 * Fallback implementation of Task Scheduling API using `setTimeout`
 */
export const scheduleWithTimeout = (task, options) => {
    if (options.signal?.aborted) {
        return () => {
            // No op as the given signal is already aborted
        };
    }
    const timeoutId = window.setTimeout(task, options.delay);
    const cancelSchedule = () => {
        window.clearTimeout(timeoutId);
        options.signal?.removeEventListener('abort', cancelSchedule);
    };
    options.signal?.addEventListener('abort', cancelSchedule);
    return cancelSchedule;
};
/**
 * Prioritized Task Scheduling API
 * @see {@link https://wicg.github.io/scheduling-apis/}
 */
export const scheduleWithScheduler = (task, options) => {
    if ('scheduler' in globalThis) {
        if (!options.signal) {
            const taskController = new TaskController();
            scheduler
                .postTask(task, {
                ...options,
                signal: taskController.signal,
            })
                .catch(error => {
                if (error.name !== 'AbortError') {
                    throw error;
                }
            });
            return () => {
                taskController.abort();
            };
        }
        if (!options.signal.aborted) {
            scheduler.postTask(task, options);
        }
        return () => {
            // No Op as the task is controlled via the given signal
        };
    }
    return scheduleWithTimeout(task, options);
};
export const createScheduler = (options = {}, scheduleTask = scheduleWithTimeout) => {
    const noop = () => {
        // No op;
    };
    let cb = noop;
    let cancelSchedule = noop;
    const scheduler = Object.freeze({
        schedule(task) {
            if (options.signal?.aborted) {
                return;
            }
            cb = task;
            cancelSchedule();
            const handleTask = () => {
                if (cb === noop) {
                    return;
                }
                cb();
                cb = noop;
                cancelSchedule = noop;
                options.signal?.removeEventListener('abort', scheduler.cancel);
            };
            cancelSchedule = scheduleTask(handleTask, options);
        },
        run() {
            cancelSchedule();
            options.signal?.removeEventListener('abort', scheduler.cancel);
            if (cb === noop) {
                return;
            }
            cb();
            cb = noop;
        },
        cancel() {
            cancelSchedule();
            cancelSchedule = noop;
            cb = noop;
            options.signal?.removeEventListener('abort', scheduler.cancel);
        },
    });
    options.signal?.addEventListener('abort', scheduler.cancel);
    return scheduler;
};
//# sourceMappingURL=scheduler.js.map